/**
 * Every status Olivia shows, with its icon and its word, in one place (owner, 2026-10-25: a status is an icon plus a
 * word, never a colour alone; design 10.1). Each family maps every one of its statuses to an icon that no other status
 * of the family uses, so two statuses still look different in greyscale, and to the dictionary key of its word, which
 * carries the meaning (the icon is aria-hidden). Where a mark is too small for its word (a marker in the year view, a
 * heat-map cell), the word is in the mark's accessible name and a legend beside it lists icon and word.
 *
 * The components keep their colours (the tone stays where it was) and their logic; they take the icon and the default
 * word from here. A component may say more in the word's place (a date, a name, a count), never something else.
 *
 * Which statuses a family holds is the server's to say: web/src/__tests__/badges.test.tsx reads each family's source
 * (the CHECK constraint of its migration, or the type or constant of its domain module) and fails when the family lacks
 * one of the server's statuses or holds one the server does not define. A few families are the screen's own reading of
 * several server fields (the follow-up's proof, a basis, a discovery stage); the test names them.
 *
 * The icons are Lucide's (lucide-react, ISC licence, already a dependency; listed under About, "Other supporting
 * licences"): inline SVG, no <style>, no style attribute.
 *
 * MARKS is the small vocabulary the one-off chips use (a chip that says one thing, or two things that differ anyway).
 */

import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArchiveX,
  BadgeCheck,
  Ban,
  BookCheck,
  CalendarClock,
  CalendarX,
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleDot,
  CircleDotDashed,
  CircleHelp,
  CircleMinus,
  CirclePause,
  CirclePlay,
  CirclePlus,
  CircleSlash,
  CircleX,
  ClipboardList,
  ClipboardX,
  Clock,
  Contrast,
  Equal,
  Eye,
  FileCheck,
  FileQuestion,
  FileText,
  FileX,
  FlaskConical,
  FolderCheck,
  HandHelping,
  Hourglass,
  Inbox,
  Layers,
  ListTodo,
  Loader,
  Lock,
  LogOut,
  MailCheck,
  MailX,
  Minus,
  OctagonAlert,
  PackageCheck,
  PencilLine,
  PowerOff,
  RefreshCw,
  Scale,
  SearchX,
  Send,
  Settings,
  ShieldAlert,
  ShieldQuestion,
  Signal,
  SignalHigh,
  SignalLow,
  SignalMedium,
  SignalZero,
  Target,
  ThumbsDown,
  ThumbsUp,
  TimerOff,
  Trash2,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Undo2,
  UserMinus,
  UserX,
  Wrench,
  type LucideIcon,
} from 'lucide-react'

import type { MessageKey } from '../i18n/en'

export interface BadgeSpec {
  icon: LucideIcon
  /** The default word. A component may say more in its place (a day, a name, a count), never something else. */
  word: MessageKey
}

type Family<S extends string> = Readonly<Record<S, BadgeSpec>>

const spec = (icon: LucideIcon, word: MessageKey): BadgeSpec => ({ icon, word })

/** The statuses whose union lives on the screen (the server answers flags; the screen names the state). */
export type OccurrenceBadge =
  | 'done'
  | 'skipped'
  | 'cancelled'
  | 'paused'
  | 'overdue'
  | 'in_review'
  | 'unowned'
  | 'in_progress'
  | 'in_preparation'
  | 'due'
  | 'waiting'
  | 'performer_left'
  | 'waiting_on_left'
export type CycleMarker = 'planned' | 'in_review' | 'overdue' | 'done' | 'skipped' | 'cancelled' | 'paused'
export type ProofState = 'verified' | 'overdue' | 'received' | 'requested' | 'drafted' | 'reported' | 'not_reported'
export type BasisState = 'live' | 'awaiting' | 'retired' | 'not_yet_effective' | 'review_passed'
export type GateState = 'met' | 'not_met' | 'unknown' | 'needs_independent'
export type TextState = 'waiting' | 'extracted' | 'not_checkable' | 'failed'
export type HeatCellState = 'A' | 'B' | 'C' | 'D' | 'not_determined' | 'not_assessed' | 'not_applicable'
export type HeatMarker = 'breach' | 'partly' | 'capped'

export const BADGES = {
  // ── The annual cycle ─────────────────────────────────────────────────────────────────────────────────────────
  /** A cycle item's chips (lib/cycle.ts occurrenceChips): its status, its derived state, waiting, someone who left. */
  occurrence: {
    done: spec(CircleCheck, 'state.done'),
    skipped: spec(CircleMinus, 'state.skipped'),
    cancelled: spec(CircleX, 'state.cancelled'),
    paused: spec(CirclePause, 'state.paused'),
    overdue: spec(TriangleAlert, 'state.overdue'),
    in_review: spec(Eye, 'state.inReview'),
    unowned: spec(UserX, 'state.unowned'),
    in_progress: spec(CirclePlay, 'state.inProgress'),
    in_preparation: spec(Loader, 'state.inPreparation'),
    due: spec(CalendarClock, 'state.due'),
    waiting: spec(Clock, 'state.waiting'),
    performer_left: spec(UserMinus, 'state.performerLeft'),
    waiting_on_left: spec(LogOut, 'state.waitingOnLeft'),
  } satisfies Family<OccurrenceBadge>,
  /** The year view's and the month grid's marker: too small for its word, which is in its name and in the legend. */
  cycleMarker: {
    planned: spec(Circle, 'occurrenceStatus.planned'),
    in_review: spec(Eye, 'state.inReview'),
    overdue: spec(TriangleAlert, 'state.overdue'),
    done: spec(CircleCheck, 'state.done'),
    skipped: spec(CircleMinus, 'state.skipped'),
    cancelled: spec(CircleX, 'state.cancelled'),
    paused: spec(CirclePause, 'state.paused'),
  } satisfies Family<CycleMarker>,
  /** A cycle template (migration 010). */
  templateStatus: {
    draft: spec(PencilLine, 'template.label.draft'),
    baseline: spec(Layers, 'template.label.baseline'),
    confirmed: spec(CircleCheck, 'template.label.adopted'),
    retired: spec(Archive, 'template.label.retired'),
  } satisfies Family<'draft' | 'baseline' | 'confirmed' | 'retired'>,

  // ── The client's acts ────────────────────────────────────────────────────────────────────────────────────────
  /** An action, on the register, on hand-over and in the follow-up (migrations 012, 051). */
  actionStatus: {
    open: spec(Circle, 'actionStatus.open'),
    in_progress: spec(CirclePlay, 'actionStatus.in_progress'),
    reported_done: spec(MailCheck, 'actionStatus.reported_done'),
    remediated: spec(Wrench, 'actionStatus.remediated'),
    verified: spec(BadgeCheck, 'actionStatus.verified'),
    closed: spec(Archive, 'actionStatus.closed'),
    cancelled: spec(CircleX, 'actionStatus.cancelled'),
  } satisfies Family<'open' | 'in_progress' | 'reported_done' | 'remediated' | 'verified' | 'closed' | 'cancelled'>,
  /** A finding's or an action's severity (migration 012). */
  severity: {
    critical: spec(OctagonAlert, 'severity.critical'),
    high: spec(TriangleAlert, 'severity.high'),
    medium: spec(CircleAlert, 'severity.medium'),
    low: spec(ChevronDown, 'severity.low'),
  } satisfies Family<'critical' | 'high' | 'medium' | 'low'>,
  /** The follow-up's proof of an action (FollowupParts: the screen's reading of the loop's flags). */
  proof: {
    verified: spec(BadgeCheck, 'followup.state.verified'),
    overdue: spec(TriangleAlert, 'followup.state.overdue'),
    received: spec(PackageCheck, 'followup.state.received'),
    requested: spec(Send, 'followup.state.requested'),
    drafted: spec(PencilLine, 'followup.state.drafted'),
    reported: spec(MailCheck, 'followup.state.reported'),
    not_reported: spec(CircleDashed, 'followup.state.notReported'),
  } satisfies Family<ProofState>,

  // ── The analysis ─────────────────────────────────────────────────────────────────────────────────────────────
  /** An analysis (migration 029). */
  assessmentStatus: {
    planning: spec(ClipboardList, 'assessmentStatus.planning'),
    gathering: spec(Inbox, 'assessmentStatus.gathering'),
    assessing: spec(Scale, 'assessmentStatus.assessing'),
    in_review: spec(Eye, 'assessmentStatus.in_review'),
    frozen: spec(Lock, 'assessmentStatus.frozen'),
    withdrawn: spec(CircleX, 'assessmentStatus.withdrawn'),
  } satisfies Family<'planning' | 'gathering' | 'assessing' | 'in_review' | 'frozen' | 'withdrawn'>,
  /** A practice's scope in an analysis (migration 029). */
  scope: {
    in_scope: spec(Target, 'scope.in_scope'),
    not_applicable: spec(CircleSlash, 'scope.not_applicable'),
    not_assessed: spec(CircleDashed, 'scope.not_assessed'),
  } satisfies Family<'in_scope' | 'not_applicable' | 'not_assessed'>,
  /** A request to the client (migration 029). */
  requestStatus: {
    draft: spec(PencilLine, 'requestStatus.draft'),
    sent: spec(Send, 'requestStatus.sent'),
    closed: spec(Archive, 'requestStatus.closed'),
  } satisfies Family<'draft' | 'sent' | 'closed'>,
  /** A line of a request (migration 029). */
  lineStatus: {
    asked: spec(Send, 'lineStatus.asked'),
    received: spec(PackageCheck, 'lineStatus.received'),
    not_held: spec(FileX, 'lineStatus.not_held'),
    not_applicable: spec(CircleSlash, 'lineStatus.not_applicable'),
    withdrawn: spec(Undo2, 'lineStatus.withdrawn'),
  } satisfies Family<'asked' | 'received' | 'not_held' | 'not_applicable' | 'withdrawn'>,
  /** An inconsistency between what the client says and what a document shows (migration 029). */
  inconsistencyState: {
    open: spec(CircleAlert, 'inconsistencyState.open'),
    resolved_by_fact: spec(CircleCheck, 'inconsistencyState.resolved_by_fact'),
    disposed: spec(Archive, 'inconsistencyState.disposed'),
  } satisfies Family<'open' | 'resolved_by_fact' | 'disposed'>,
  /** A practice's maturity level, 0 to 4 (rule maturity.v1): one more bar per level. */
  level: {
    0: spec(SignalZero, 'level.0'),
    1: spec(SignalLow, 'level.1'),
    2: spec(SignalMedium, 'level.2'),
    3: spec(SignalHigh, 'level.3'),
    4: spec(Signal, 'level.4'),
  } satisfies Family<'0' | '1' | '2' | '3' | '4'>,
  /** A recorded fact's value (migration 032). */
  factValue: {
    yes: spec(CircleCheck, 'factValue.yes'),
    partial: spec(Contrast, 'factValue.partial'),
    no: spec(CircleX, 'factValue.no'),
    unknown: spec(CircleHelp, 'factValue.unknown'),
  } satisfies Family<'yes' | 'partial' | 'no' | 'unknown'>,
  /** A legal duty's conclusion (library/engine.ts DUTY_VALUES). */
  dutyValue: {
    met: spec(CircleCheck, 'dutyValue.met'),
    partly_met: spec(Contrast, 'dutyValue.partly_met'),
    not_met: spec(CircleX, 'dutyValue.not_met'),
    not_determined: spec(CircleHelp, 'dutyValue.not_determined'),
  } satisfies Family<'met' | 'partly_met' | 'not_met' | 'not_determined'>,
  /** A level's gate (domain/maturity.ts outcome), and a gate that needs an independent assessment. */
  gate: {
    met: spec(CircleCheck, 'engine.gate.met'),
    not_met: spec(CircleX, 'engine.gate.not_met'),
    unknown: spec(CircleHelp, 'engine.gate.unknown'),
    needs_independent: spec(ShieldQuestion, 'engine.needsIndependent'),
  } satisfies Family<GateState>,
  /** An element of a practice (domain/duty.ts ElementStatus). */
  elementStatus: {
    ok: spec(CircleCheck, 'elementStatus.ok'),
    absent: spec(CircleX, 'elementStatus.absent'),
    not_applicable: spec(CircleSlash, 'elementStatus.not_applicable'),
    unverified: spec(FileQuestion, 'elementStatus.unverified'),
    unchecked: spec(CircleDashed, 'elementStatus.unchecked'),
  } satisfies Family<'ok' | 'absent' | 'not_applicable' | 'unverified' | 'unchecked'>,
  /** Readiness for a rule not yet in force (domain/duty.ts). */
  readiness: {
    met_already: spec(CircleCheck, 'engine.readiness.met_already'),
    not_yet: spec(Hourglass, 'engine.readiness.not_yet'),
  } satisfies Family<'met_already' | 'not_yet'>,
  /** A key risk indicator's reading against its thresholds (migration 032). */
  kriPosition: {
    within: spec(CircleCheck, 'kriPosition.within'),
    warning: spec(TriangleAlert, 'kriPosition.warning'),
    breach: spec(OctagonAlert, 'kriPosition.breach'),
    no_threshold: spec(Minus, 'kriPosition.no_threshold'),
  } satisfies Family<'within' | 'warning' | 'breach' | 'no_threshold'>,
  /** A sample test (migration 032). */
  sampleStatus: {
    planned: spec(ClipboardList, 'sampleStatus.planned'),
    tested: spec(FlaskConical, 'sampleStatus.tested'),
    reviewed: spec(BadgeCheck, 'sampleStatus.reviewed'),
  } satisfies Family<'planned' | 'tested' | 'reviewed'>,
  /** A sample test's conclusion (migration 032). */
  testConclusion: {
    effective: spec(CircleCheck, 'testConclusion.effective'),
    not_effective: spec(CircleX, 'testConclusion.not_effective'),
    not_concluded: spec(CircleDashed, 'testConclusion.not_concluded'),
  } satisfies Family<'effective' | 'not_effective' | 'not_concluded'>,
  /** A gap's priority (library/engine.ts PRIORITIES). */
  priority: {
    P1: spec(ChevronsUp, 'priority.P1'),
    P2: spec(ChevronUp, 'priority.P2'),
    P3: spec(Minus, 'priority.P3'),
    P4: spec(ChevronDown, 'priority.P4'),
  } satisfies Family<'P1' | 'P2' | 'P3' | 'P4'>,
  /** A model's states on the business page (domain/inherent.ts ModelState). */
  modelState: {
    validation_due: spec(CalendarX, 'modelState.validation_due'),
    changed_since_validation: spec(RefreshCw, 'modelState.changed_since_validation'),
    not_confirmed: spec(CircleHelp, 'modelState.not_confirmed'),
    vendor_defaults_not_assessed: spec(Settings, 'modelState.vendor_defaults_not_assessed'),
  } satisfies Family<'validation_due' | 'changed_since_validation' | 'not_confirmed' | 'vendor_defaults_not_assessed'>,
  /** A change between two analyses (domain/delta.ts CHANGES). */
  change: {
    improved: spec(TrendingUp, 'progress.change.improved'),
    worsened: spec(TrendingDown, 'progress.change.worsened'),
    unchanged: spec(Equal, 'progress.change.unchanged'),
    newly_in_scope: spec(CirclePlus, 'progress.change.newly_in_scope'),
    left_scope: spec(CircleMinus, 'progress.change.left_scope'),
    not_comparable: spec(CircleSlash, 'progress.change.not_comparable'),
    undetermined: spec(CircleHelp, 'progress.change.undetermined'),
    not_reassessed: spec(CircleDashed, 'progress.change.not_reassessed'),
  } satisfies Family<'improved' | 'worsened' | 'unchanged' | 'newly_in_scope' | 'left_scope' | 'not_comparable' | 'undetermined' | 'not_reassessed'>,
  /** Whether a regime applies, as screened (migration 041). */
  applies: {
    yes: spec(CircleCheck, 'regulation.applies.yes'),
    no: spec(CircleSlash, 'regulation.applies.no'),
    not_determined: spec(CircleHelp, 'regulation.applies.not_determined'),
  } satisfies Family<'yes' | 'no' | 'not_determined'>,
  /** The capability heat map's cell: its band against target, or why it has none (data/rules/heat.json). */
  heatCell: {
    A: spec(CircleCheck, 'heat.cell.A'),
    B: spec(ChevronDown, 'heat.cell.B'),
    C: spec(ChevronsDown, 'heat.cell.C'),
    D: spec(OctagonAlert, 'heat.cell.D'),
    not_determined: spec(CircleHelp, 'heat.cell.not_determined'),
    not_assessed: spec(CircleDashed, 'heat.cell.not_assessed'),
    not_applicable: spec(CircleSlash, 'heat.cell.not_applicable'),
  } satisfies Family<HeatCellState>,
  /** The heat map's markers on a cell (data/rules/heat.json). */
  heatMarker: {
    breach: spec(TriangleAlert, 'heat.marker.breach'),
    partly: spec(Contrast, 'heat.marker.partly'),
    capped: spec(Lock, 'heat.marker.capped'),
  } satisfies Family<HeatMarker>,

  // ── Mandate and roles ────────────────────────────────────────────────────────────────────────────────────────
  /** An item of an itemised check, and a point of an agreement (migration 026). */
  itemState: {
    unchecked: spec(CircleDashed, 'itemState.unchecked'),
    found: spec(CircleCheck, 'itemState.found'),
    missing: spec(SearchX, 'itemState.missing'),
    not_applicable: spec(CircleSlash, 'itemState.not_applicable'),
  } satisfies Family<'unchecked' | 'found' | 'missing' | 'not_applicable'>,
  /** A start check (migration 026). */
  checkState: {
    open: spec(Circle, 'checkState.open'),
    done: spec(CircleCheck, 'checkState.done'),
    not_applicable: spec(CircleSlash, 'checkState.not_applicable'),
    exception: spec(ShieldAlert, 'checkState.exception'),
  } satisfies Family<'open' | 'done' | 'not_applicable' | 'exception'>,
  /** Whether a provider may hold a role (migration 026). */
  allowed: {
    never: spec(Ban, 'mandate.allowed.never'),
    with_basis: spec(FileText, 'mandate.allowed.with_basis'),
    yes: spec(CircleCheck, 'mandate.allowed.yes'),
  } satisfies Family<'never' | 'with_basis' | 'yes'>,
  /** What a held role still needs (domain/mandate.ts RoleState); three of the words take a date. */
  roleState: {
    basis_not_confirmed: spec(FileQuestion, 'roleState.basis_not_confirmed'),
    appointment_not_recorded: spec(ClipboardX, 'roleState.appointment_not_recorded'),
    notice_not_recorded: spec(MailX, 'roleState.notice_not_recorded'),
    rule_changes: spec(CalendarClock, 'roleState.rule_changes'),
    must_end_or_become_supported: spec(OctagonAlert, 'roleState.must_end_or_become_supported'),
    tasks_rule_changes: spec(ListTodo, 'roleState.tasks_rule_changes'),
  } satisfies Family<'basis_not_confirmed' | 'appointment_not_recorded' | 'notice_not_recorded' | 'rule_changes' | 'must_end_or_become_supported' | 'tasks_rule_changes'>,

  // ── Review, hand-over and AI ─────────────────────────────────────────────────────────────────────────────────
  /** A deliverable (migration 034). */
  deliverableStatus: {
    draft: spec(PencilLine, 'deliverableStatus.draft'),
    in_review: spec(Eye, 'deliverableStatus.in_review'),
    approved: spec(BadgeCheck, 'deliverableStatus.approved'),
    published: spec(BookCheck, 'deliverableStatus.published'),
  } satisfies Family<'draft' | 'in_review' | 'approved' | 'published'>,
  /** A client checkpoint on a deliverable (migration 034). */
  checkpointStatus: {
    pending: spec(Clock, 'checkpointStatus.pending'),
    approved: spec(CircleCheck, 'checkpointStatus.approved'),
    rejected: spec(CircleX, 'checkpointStatus.rejected'),
    expired: spec(TimerOff, 'checkpointStatus.expired'),
  } satisfies Family<'pending' | 'approved' | 'rejected' | 'expired'>,
  /** A hand-off to another product (migration 055). */
  handoffStatus: {
    draft: spec(PencilLine, 'handoff.status.draft'),
    in_review: spec(Eye, 'handoff.status.in_review'),
    approved: spec(BadgeCheck, 'handoff.status.approved'),
    sent: spec(Send, 'handoff.status.sent'),
    answered: spec(MailCheck, 'handoff.status.answered'),
    withdrawn: spec(Undo2, 'handoff.status.withdrawn'),
  } satisfies Family<'draft' | 'in_review' | 'approved' | 'sent' | 'answered' | 'withdrawn'>,
  /** A suggested next step (domain/nextstep.ts SuggestionState). */
  nextState: {
    offered: spec(HandHelping, 'next.state.offered'),
    not_yet_offered: spec(Clock, 'next.state.not_yet_offered'),
    not_yet_available: spec(Ban, 'next.state.not_yet_available'),
    done: spec(CircleCheck, 'next.state.done'),
  } satisfies Family<'offered' | 'not_yet_offered' | 'not_yet_available' | 'done'>,
  /** A discovery stage (lib/discover.ts stageState: the screen's reading of the analysis and its counts). */
  stage: {
    not_started: spec(Circle, 'discover.state.not_started'),
    in_progress: spec(CirclePlay, 'discover.state.in_progress'),
    done: spec(CircleCheck, 'discover.state.done'),
    waiting: spec(Clock, 'discover.state.waiting'),
    provisional: spec(CircleDotDashed, 'discover.state.provisional'),
    review: spec(Eye, 'discover.state.review'),
    closed: spec(Lock, 'discover.state.closed'),
  } satisfies Family<'not_started' | 'in_progress' | 'done' | 'waiting' | 'provisional' | 'review' | 'closed'>,
  /** An AI proposal, and a draft of the second take (migration 056; a refused call is shown as its own line). */
  proposalStatus: {
    pending: spec(Clock, 'proposals.status.pending'),
    accepted: spec(CircleCheck, 'proposals.status.accepted'),
    rejected: spec(CircleX, 'proposals.status.rejected'),
    stale: spec(TimerOff, 'proposals.status.stale'),
  } satisfies Family<'pending' | 'accepted' | 'rejected' | 'stale'>,
  /** A document in the evidence inbox (migration 056). */
  inboxState: {
    inbox: spec(Inbox, 'evinbox.state.inbox'),
    filed: spec(FolderCheck, 'evinbox.state.filed'),
    set_aside: spec(ArchiveX, 'evinbox.state.set_aside'),
  } satisfies Family<'inbox' | 'filed' | 'set_aside'>,
  /** Whether two takes on a fact agree (migration 056). */
  agreement: {
    agree: spec(ThumbsUp, 'secondOpinion.agree'),
    disagree: spec(ThumbsDown, 'secondOpinion.disagree'),
  } satisfies Family<'agree' | 'disagree'>,
  /** A round of interview proposals (migration 057). */
  roundStatus: {
    open: spec(CircleDot, 'round.status.open'),
    closed: spec(Lock, 'round.status.closed'),
  } satisfies Family<'open' | 'closed'>,
  /** A flag of the second take (migration 058). */
  secondTakeStatus: {
    open: spec(CircleAlert, 'secondTake.status.open'),
    accepted: spec(CircleCheck, 'secondTake.status.accepted'),
    rejected: spec(CircleX, 'secondTake.status.rejected'),
  } satisfies Family<'open' | 'accepted' | 'rejected'>,
  /** A module run (migration 054). */
  moduleRun: {
    draft: spec(PencilLine, 'modules.runStatus.draft'),
    made_deliverable: spec(FileCheck, 'modules.runStatus.made_deliverable'),
    discarded: spec(Trash2, 'modules.runStatus.discarded'),
  } satisfies Family<'draft' | 'made_deliverable' | 'discarded'>,
  /** A client's AI route to a model (services/client-routes.ts RouteState). */
  aiRoute: {
    off: spec(PowerOff, 'aic.state.off'),
    waiting: spec(Clock, 'aic.state.waiting'),
    in_force: spec(CircleCheck, 'aic.state.in_force'),
    not_named: spec(FileQuestion, 'aic.state.not_named'),
    ended: spec(CircleX, 'aic.state.ended'),
  } satisfies Family<'off' | 'waiting' | 'in_force' | 'not_named' | 'ended'>,
  /** A model call (migration 007). */
  aiCall: {
    sent: spec(Send, 'explain.status.sent'),
    refused: spec(Ban, 'explain.status.refused'),
    failed: spec(CircleX, 'explain.status.failed'),
  } satisfies Family<'sent' | 'refused' | 'failed'>,
  /** A client declared synthetic (routes/client/index.ts; 'none' shows no chip). */
  synthetic: {
    declared: spec(Clock, 'synthetic.state.declared'),
    in_force: spec(FlaskConical, 'synthetic.state.in_force'),
    ended: spec(CircleX, 'synthetic.state.ended'),
  } satisfies Family<'declared' | 'in_force' | 'ended'>,
  /** A legal basis for customer data (CustomerDataPage: the screen's reading of its dates). */
  basis: {
    live: spec(CircleCheck, 'basis.state.live'),
    awaiting: spec(Clock, 'basis.state.awaiting'),
    retired: spec(Archive, 'basis.state.retired'),
    not_yet_effective: spec(CalendarClock, 'basis.state.notYetEffective'),
    review_passed: spec(CalendarX, 'basis.state.reviewPassed'),
  } satisfies Family<BasisState>,
  /** A document's text (migration 025; 'waiting' before the worker has read it). */
  textStatus: {
    waiting: spec(Clock, 'textStatus.waiting'),
    extracted: spec(FileCheck, 'textStatus.extracted'),
    not_checkable: spec(FileQuestion, 'textStatus.not_checkable'),
    failed: spec(FileX, 'textStatus.failed'),
  } satisfies Family<TextState>,
  /** A self-rating beside the evidenced level (perception_gap.v1, domain/perception.ts Reading); above and below take how many levels. */
  perception: {
    no_evidence: spec(CircleDashed, 'perception.reading.no_evidence'),
    not_in_scope: spec(CircleSlash, 'perception.reading.not_in_scope'),
    above: spec(ArrowUp, 'perception.reading.above'),
    same: spec(Equal, 'perception.reading.same'),
    below: spec(ArrowDown, 'perception.reading.below'),
  } satisfies Family<'no_evidence' | 'not_in_scope' | 'above' | 'same' | 'below'>,
} as const

export type BadgeFamily = keyof typeof BADGES
export type BadgeStatus<F extends BadgeFamily> = keyof (typeof BADGES)[F] & string

/** The badge of a status: its icon and its word. */
export function badgeOf<F extends BadgeFamily>(family: F, status: BadgeStatus<F>): BadgeSpec {
  return (BADGES[family] as Record<string, BadgeSpec>)[status] as BadgeSpec
}

/**
 * The marks of the one-off chips: a chip that says one thing ("confirmed", "AI draft"), or one of two that differ in
 * their words and shapes anyway. A status of a family takes its family's badge instead.
 */
export const MARKS = {
  overdue: TriangleAlert,
  unowned: UserX,
  review: Eye,
  progress: CirclePlay,
  preparing: Loader,
  due: CalendarClock,
  done: CircleCheck,
  skipped: CircleMinus,
  cancelled: CircleX,
  waiting: Clock,
  paused: CirclePause,
  none: CircleDashed,
} as const satisfies Record<string, LucideIcon>

export type MarkName = keyof typeof MARKS
